/**
 * PickupPage - Pickup Terminal for tracking and marking orders as picked up
 * Features:
 * - View orders by status (ready, preparing, pending)
 * - Search by order number or customer name
 * - Mark orders as picked up
 * - Call customer notification
 * - Real-time updates
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Package, Search, Check, Clock, ChefHat, Bell, RefreshCw,
  User, Phone, Coffee, Loader2, Sun, Moon, AlertCircle, X,
  Volume2, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';
import { useTheme } from '@/context/ThemeContext';

// Order Card Component
const OrderCard = ({ order, onPickup, onCall, loading }) => {
  const getStatusStyle = () => {
    switch (order.status) {
      case 'ready':
        return 'border-green-500 bg-green-50 dark:bg-green-900/20';
      case 'preparing':
        return 'border-amber-500 bg-amber-50 dark:bg-amber-900/20';
      default:
        return 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800';
    }
  };

  const getStatusBadge = () => {
    switch (order.status) {
      case 'ready':
        return (
          <span className="px-3 py-1 bg-green-500 text-white text-sm font-bold rounded-full flex items-center gap-1 animate-pulse">
            <Check className="w-4 h-4" /> READY
          </span>
        );
      case 'preparing':
        return (
          <span className="px-3 py-1 bg-amber-500 text-white text-sm font-medium rounded-full flex items-center gap-1">
            <ChefHat className="w-4 h-4" /> Preparing
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-gray-400 text-white text-sm font-medium rounded-full flex items-center gap-1">
            <Clock className="w-4 h-4" /> Pending
          </span>
        );
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div 
      className={`rounded-2xl border-2 p-5 transition-all ${getStatusStyle()}`}
      data-testid={`pickup-order-${order.order_number}`}
    >
      {/* Order Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-4xl font-bold text-gray-900 dark:text-white mb-1">
            #{order.order_number || '—'}
          </div>
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
            <User className="w-4 h-4" />
            <span className="font-medium">{order.customer_name || 'Guest'}</span>
          </div>
        </div>
        {getStatusBadge()}
      </div>

      {/* Order Items */}
      <div className="space-y-1 mb-4 text-sm text-gray-700 dark:text-gray-300">
        {order.items?.slice(0, 4).map((item, i) => (
          <div key={i} className="flex justify-between">
            <span>{item.quantity}x {item.name}</span>
            <span className="text-gray-500">${(item.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        {order.items?.length > 4 && (
          <div className="text-gray-500 italic">+{order.items.length - 4} more items</div>
        )}
      </div>

      {/* Order Info */}
      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-4">
        <span>Ordered: {formatTime(order.created_at)}</span>
        {order.order_type && <span className="capitalize">{order.order_type.replace('_', ' ')}</span>}
        {order.call_count > 0 && (
          <span className="text-amber-600">Called {order.call_count}x</span>
        )}
      </div>

      {/* Total */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="text-2xl font-bold text-gray-900 dark:text-white">
          ${order.total?.toFixed(2)}
        </div>
        
        <div className="flex gap-2">
          {order.status === 'ready' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCall(order.id)}
                disabled={loading === `call-${order.id}`}
                className="text-amber-600 border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                data-testid={`call-order-${order.order_number}`}
              >
                {loading === `call-${order.id}` ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </Button>
              <Button
                onClick={() => onPickup(order.id)}
                disabled={loading === `pickup-${order.id}`}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid={`pickup-order-${order.order_number}`}
              >
                {loading === `pickup-${order.id}` ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Picked Up
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Pickup Terminal Component
export default function PickupPage() {
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  // State
  const [orders, setOrders] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [preparingOrders, setPreparingOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [summary, setSummary] = useState({ total: 0, ready_count: 0, preparing_count: 0, pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [activeTab, setActiveTab] = useState('ready');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get('/api/pickup/orders');
      setOrders(res.data.orders || []);
      setReadyOrders(res.data.ready || []);
      setPreparingOrders(res.data.preparing || []);
      setPendingOrders(res.data.pending || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search orders
  const handleSearch = async () => {
    if (!search.trim()) {
      setSearchResults(null);
      return;
    }
    
    try {
      const isNumber = /^\d+$/.test(search.trim());
      const url = isNumber 
        ? `/api/pickup/orders/search?order_number=${search.trim()}`
        : `/api/pickup/orders/search?customer_name=${encodeURIComponent(search.trim())}`;
      
      const res = await api.get(url);
      setSearchResults(res.data.orders || []);
    } catch (err) {
      toast.error('Search failed');
    }
  };

  const clearSearch = () => {
    setSearch('');
    setSearchResults(null);
  };

  // Mark as picked up
  const handlePickup = async (orderId) => {
    setActionLoading(`pickup-${orderId}`);
    try {
      const res = await api.post(`/api/pickup/orders/${orderId}/mark-picked-up`);
      toast.success(res.data.message, {
        icon: <CheckCircle2 className="w-5 h-5 text-green-500" />
      });
      fetchOrders();
      
      if (searchResults) {
        setSearchResults(prev => prev.filter(o => o.id !== orderId));
      }
    } catch (err) {
      toast.error('Failed to mark as picked up');
    } finally {
      setActionLoading(null);
    }
  };

  // Call customer
  const handleCall = async (orderId) => {
    setActionLoading(`call-${orderId}`);
    try {
      const res = await api.post(`/api/pickup/orders/${orderId}/call-customer`);
      toast.success(res.data.message, {
        icon: <Volume2 className="w-5 h-5 text-amber-500" />
      });
      fetchOrders();
    } catch (err) {
      toast.error('Failed to call customer');
    } finally {
      setActionLoading(null);
    }
  };

  // Auto-refresh
  useEffect(() => {
    fetchOrders();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchOrders, 15000); // Every 15 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchOrders, autoRefresh]);

  // Get current tab orders
  const getCurrentOrders = () => {
    if (searchResults) return searchResults;
    switch (activeTab) {
      case 'ready': return readyOrders;
      case 'preparing': return preparingOrders;
      case 'pending': return pendingOrders;
      default: return orders;
    }
  };

  const currentOrders = getCurrentOrders();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900" data-testid="pickup-terminal">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <Package className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pickup Station</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {summary.ready_count} ready • {summary.preparing_count} preparing
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Order # or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 w-64 bg-gray-50 dark:bg-gray-700"
                data-testid="pickup-search-input"
              />
              {search && (
                <button 
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button onClick={handleSearch} variant="outline" data-testid="pickup-search-btn">
              Search
            </Button>

            {/* Controls */}
            <label className="flex items-center gap-2 text-sm text-gray-500">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              Auto-refresh
            </label>
            <Button variant="outline" size="icon" onClick={fetchOrders}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode}>
              {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Search Results Banner */}
      {searchResults && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <span className="text-blue-700 dark:text-blue-400">
              Found {searchResults.length} order(s) matching "{search}"
            </span>
            <Button variant="ghost" size="sm" onClick={clearSearch}>
              Clear search
            </Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      {!searchResults && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6">
          <div className="max-w-7xl mx-auto flex gap-1">
            <button
              onClick={() => setActiveTab('ready')}
              className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'ready'
                  ? 'border-green-500 text-green-600 dark:text-green-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              data-testid="pickup-tab-ready"
            >
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                Ready
                {summary.ready_count > 0 && (
                  <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                    {summary.ready_count}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('preparing')}
              className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'preparing'
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              data-testid="pickup-tab-preparing"
            >
              <div className="flex items-center gap-2">
                <ChefHat className="w-4 h-4" />
                Preparing
                {summary.preparing_count > 0 && (
                  <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                    {summary.preparing_count}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'pending'
                  ? 'border-gray-500 text-gray-600 dark:text-gray-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              data-testid="pickup-tab-pending"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Pending
                {summary.pending_count > 0 && (
                  <span className="px-2 py-0.5 bg-gray-400 text-white text-xs rounded-full">
                    {summary.pending_count}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Orders Grid */}
      <div className="max-w-7xl mx-auto p-6">
        {currentOrders.length === 0 ? (
          <div className="text-center py-16">
            {activeTab === 'ready' ? (
              <>
                <CheckCircle2 className="w-16 h-16 text-green-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300 mb-2">No orders ready</h3>
                <p className="text-gray-500">Orders will appear here when they're ready for pickup</p>
              </>
            ) : activeTab === 'preparing' ? (
              <>
                <ChefHat className="w-16 h-16 text-amber-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300 mb-2">Nothing preparing</h3>
                <p className="text-gray-500">Orders being made will appear here</p>
              </>
            ) : (
              <>
                <Coffee className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300 mb-2">No pending orders</h3>
                <p className="text-gray-500">New orders will appear here</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentOrders.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                onPickup={handlePickup}
                onCall={handleCall}
                loading={actionLoading}
              />
            ))}
          </div>
        )}
      </div>

      {/* Ready Orders Counter - Large Display */}
      {readyOrders.length > 0 && activeTab === 'ready' && (
        <div className="fixed bottom-0 left-0 right-0 bg-green-500 text-white py-4 px-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 animate-bounce" />
              <span className="text-lg font-medium">
                {readyOrders.length} order{readyOrders.length > 1 ? 's' : ''} ready for pickup!
              </span>
            </div>
            <div className="text-2xl font-bold">
              {readyOrders.filter(o => o.order_number).map(o => `#${o.order_number}`).join(' • ')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
